import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Package, DollarSign } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ProductsPage = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  // Carregar produtos reais do Firestore
  useEffect(() => {
    loadProducts();
  }, [user]);

  const loadProducts = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/business/${user.id}/products`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setProducts(data.products || []);
        } else {
          console.error('Erro ao carregar produtos:', data.error);
        }
      } else {
        console.error('Erro na requisição de produtos:', response.status);
      }
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = () => {
    setEditingProduct(null);
    setShowModal(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setShowModal(true);
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Tem certeza que deseja deletar este produto?')) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/business/${user.id}/products/${productId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Remover produto da lista local
          setProducts(products.filter(p => p.id !== productId));
        } else {
          alert('Erro ao deletar produto: ' + data.error);
        }
      } else {
        alert('Erro ao deletar produto');
      }
    } catch (error) {
      console.error('Erro ao deletar produto:', error);
      alert('Erro ao deletar produto');
    }
  };

  const [discoveryProgress, setDiscoveryProgress] = useState(null);
  const [isDiscovering, setIsDiscovering] = useState(false);

  // Verificar progresso do discovery
  const checkDiscoveryProgress = async () => {
    if (!user?.id) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/business/${user.id}/discovery/progress`);
      if (response.ok) {
        const progress = await response.json();
        setDiscoveryProgress(progress);
        setIsDiscovering(progress.is_running);
        
        if (progress.status === 'completed') {
          setIsDiscovering(false);
          loadProducts();
        }
      }
    } catch (error) {
      console.error('Erro ao verificar progresso:', error);
    }
  };

  // Verificar progresso periodicamente
  useEffect(() => {
    if (isDiscovering) {
      const interval = setInterval(checkDiscoveryProgress, 3000);
      return () => clearInterval(interval);
    }
  }, [isDiscovering, user?.id]);

  // Verificar se há discovery em andamento ao carregar a página
  useEffect(() => {
    checkDiscoveryProgress();
  }, [user?.id]);

  const handleDiscoverProducts = async () => {
    if (!user?.id) {
      alert('Usuário não autenticado');
      return;
    }

    if (isDiscovering) {
      alert('Discovery já está em andamento. Aguarde a conclusão.');
      return;
    }

    if (!window.confirm('Isso irá buscar produtos do seu site. Deseja continuar?')) {
      return;
    }

    setIsDiscovering(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/business/${user.id}/discovery/products/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Iniciar monitoramento do progresso
          checkDiscoveryProgress();
        } else {
          alert('Erro no Discovery: ' + data.error);
          setIsDiscovering(false);
        }
      } else {
        alert('Erro ao iniciar Discovery de produtos');
        setIsDiscovering(false);
      }
    } catch (error) {
      console.error('Erro no Discovery:', error);
      alert('Erro ao iniciar Discovery de produtos');
      setIsDiscovering(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary-50">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-secondary-900 font-display">
              Produtos
            </h1>
            <p className="text-secondary-600 mt-1">
              Gerencie o catálogo de produtos do seu negócio
            </p>
          </div>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 mt-4 sm:mt-0">
            <button
              onClick={handleDiscoverProducts}
              className={`btn-secondary flex items-center space-x-2 ${isDiscovering ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={loading || isDiscovering}
            >
              <Package className="w-4 h-4" />
              <span>{isDiscovering ? 'Descobrindo...' : 'Buscar Produtos do Site'}</span>
            </button>
            <button
              onClick={handleAddProduct}
              className="btn-primary flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Adicionar Produto</span>
            </button>
          </div>
        </div>

        {/* Barra de Progresso do Discovery */}
        {isDiscovering && discoveryProgress && (
          <div className="card mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-secondary-900">
                🔍 Descobrindo produtos do seu site...
              </h3>
              <span className="text-sm text-secondary-600">
                {discoveryProgress.progress?.productsProcessed || 0} / {discoveryProgress.progress?.totalProducts || '?'} produtos
              </span>
            </div>
            
            <div className="w-full bg-secondary-200 rounded-full h-3 mb-4">
              <div 
                className="bg-primary-600 h-3 rounded-full transition-all duration-300"
                style={{ width: `${discoveryProgress.percentage || 0}%` }}
              ></div>
            </div>
            
            <div className="flex justify-between text-sm text-secondary-600">
              <span>
                Lote {discoveryProgress.progress?.currentBatch || 0} de {discoveryProgress.progress?.totalBatches || 0}
              </span>
              <span>
                {Math.round(discoveryProgress.percentage || 0)}% concluído
              </span>
            </div>
            
            <p className="text-sm text-secondary-500 mt-2">
              ⏱️ Processando em lotes para garantir estabilidade. Aguarde...
            </p>
          </div>
        )}

        {/* Status de Discovery Concluído */}
        {!isDiscovering && discoveryProgress?.status === 'completed' && discoveryProgress?.products_found > 0 && (
          <div className="card mb-6 bg-green-50 border-green-200">
            <div className="flex items-center space-x-3">
              <div className="bg-green-100 p-2 rounded-full">
                <Package className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-green-900">
                  ✅ Discovery concluído com sucesso!
                </h3>
                <p className="text-green-700">
                  {discoveryProgress.products_found} produtos foram encontrados e adicionados ao seu catálogo.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Lista de Produtos */}
        {products.length === 0 ? (
          <div className="card text-center py-12">
            <Package className="w-16 h-16 text-secondary-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 mb-2">
              Nenhum produto cadastrado
            </h3>
            <p className="text-secondary-600 mb-6">
              Adicione seus primeiros produtos para que o assistente possa vendê-los
            </p>
            <button
              onClick={handleAddProduct}
              className="btn-primary"
            >
              Adicionar Primeiro Produto
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product) => (
              <div key={product.id} className="card-hover">
                <div className="flex justify-between items-start mb-4">
                  <div className="bg-primary-100 p-2 rounded-lg">
                    <Package className="w-6 h-6 text-primary-600" />
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditProduct(product)}
                      className="text-secondary-400 hover:text-primary-600 transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => handleDeleteProduct(product.id)}
                      className="text-secondary-400 hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                  {product.name}
                </h3>
                <p className="text-secondary-600 text-sm mb-4">
                  {product.description}
                </p>
                
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-1">
                    <DollarSign className="w-4 h-4 text-green-600" />
                    <span className="text-lg font-bold text-green-600">
                      R$ {product.price.toFixed(2)}
                    </span>
                  </div>
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                    {product.category}
                  </span>
                </div>
                
                {/* Link de Compra */}
                {product.url && (
                  <div className="mt-3">
                    <a
                      href={product.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 text-sm font-medium transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      <span>Ver no Site</span>
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Modal de Produto */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h2 className="text-xl font-bold text-secondary-900 mb-4">
                {editingProduct ? 'Editar Produto' : 'Adicionar Produto'}
              </h2>
              
              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Nome do Produto
                  </label>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="Ex: Smartphone Galaxy Pro"
                    defaultValue={editingProduct?.name || ''}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Descrição
                  </label>
                  <textarea
                    className="input-field"
                    rows="3"
                    placeholder="Descreva o produto..."
                    defaultValue={editingProduct?.description || ''}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Preço (R$)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      className="input-field"
                      placeholder="0,00"
                      defaultValue={editingProduct?.price || ''}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Categoria
                    </label>
                    <select className="input-field" defaultValue={editingProduct?.category || ''}>
                      <option value="">Selecione...</option>
                      <option value="Eletrônicos">Eletrônicos</option>
                      <option value="Informática">Informática</option>
                      <option value="Casa">Casa</option>
                      <option value="Moda">Moda</option>
                      <option value="Outros">Outros</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Link de Compra
                  </label>
                  <input
                    type="url"
                    className="input-field"
                    placeholder="https://..."
                    defaultValue={editingProduct?.url || ''}
                  />
                </div>
              </form>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button className="btn-primary">
                  {editingProduct ? 'Salvar' : 'Adicionar'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ProductsPage;

